// src/pages/league/JoinLeaguePage.tsx
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import apiService, { handleApiError } from '../../services/api';
import { InviteCodePreview } from '../../types';

type LeagueTypeLabel = 'Season Long' | 'Weekly Redraft';

function formatLeagueType(type: string): LeagueTypeLabel {
  if (type === 'weekly_redraft') return 'Weekly Redraft';
  return 'Season Long';
}

export default function JoinLeaguePage() {
  const { code } = useParams<{ code: string }>();
  const { user } = useAuth();
  const navigate = useNavigate();

  const [preview, setPreview] = useState<InviteCodePreview | null>(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [fetchError, setFetchError] = useState<string | null>(null);

  const [teamName, setTeamName] = useState('');
  const [joining, setJoining] = useState(false);
  const [joinError, setJoinError] = useState<string | null>(null);
  const [alreadyJoined, setAlreadyJoined] = useState(false);

  async function fetchPreview() {
    if (!code) return;
    setLoading(true);
    setNotFound(false);
    setFetchError(null);

    try {
      const data = await apiService.getInviteCodePreview(code);
      setPreview(data);
    } catch (err: any) {
      if (err?.response?.status === 404 || err?.status === 404) {
        setNotFound(true);
      } else {
        // ApiError doesn't carry response directly — check message or status
        const msg = err?.message || '';
        if (msg.includes('404') || msg.toLowerCase().includes('not found')) {
          setNotFound(true);
        } else {
          setFetchError(handleApiError(err));
        }
      }
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    if (!code) {
      setNotFound(true);
      setLoading(false);
      return;
    }
    fetchPreview();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [code]);

  async function handleJoin(e: React.FormEvent) {
    e.preventDefault();
    if (!code || !teamName.trim()) return;

    setJoining(true);
    setJoinError(null);

    try {
      const team = await apiService.joinLeagueByCode(code, teamName.trim());
      navigate(`/app/leagues/${team.league_id}`);
    } catch (err: any) {
      const msg = handleApiError(err);
      if (msg.toLowerCase().includes('already have a team')) {
        setAlreadyJoined(true);
      } else {
        setJoinError(msg);
      }
    } finally {
      setJoining(false);
    }
  }

  // ── Loading state ──
  if (loading) {
    return (
      <div className="min-h-screen bg-secondary-50 flex items-center justify-center">
        <div className="flex flex-col items-center gap-3">
          <div className="loading-spinner w-8 h-8"></div>
          <p className="text-secondary-500 text-sm">Loading league info…</p>
        </div>
      </div>
    );
  }

  // ── 404 state ──
  if (notFound) {
    return (
      <div className="min-h-screen bg-secondary-50 flex items-center justify-center px-4">
        <div className="bg-white rounded-lg shadow p-8 max-w-md w-full text-center">
          <span className="font-display text-2xl tracking-tight leading-none text-secondary-900 block mb-4">
            ONLY<span className="text-gold-700">KICKERS</span>
          </span>
          <h1 className="font-display uppercase text-2xl leading-none text-ink-900">Invite Link Not Found</h1>
          <p className="mt-2 text-secondary-600">This invite link is invalid or has expired.</p>
          <Link to="/" className="mt-6 inline-block btn-primary">
            Back to Home
          </Link>
        </div>
      </div>
    );
  }

  // ── Generic error state ──
  if (fetchError) {
    return (
      <div className="min-h-screen bg-secondary-50 flex items-center justify-center px-4">
        <div className="bg-white rounded-lg shadow p-8 max-w-md w-full text-center">
          <span className="text-5xl">⚠️</span>
          <h1 className="mt-4 font-display uppercase text-2xl leading-none text-ink-900">Something Went Wrong</h1>
          <p className="mt-2 text-secondary-600">Failed to load league information. Please try again.</p>
          <button onClick={fetchPreview} className="mt-6 btn-primary">
            Try Again
          </button>
        </div>
      </div>
    );
  }

  if (!preview) return null;

  const leagueStarted = preview.league_status !== 'setup';
  const leagueFull = preview.spots_available === 0;
  const canJoin = !leagueStarted && !leagueFull;

  return (
    <div className="min-h-screen bg-secondary-50 flex flex-col justify-center py-12 px-4 sm:px-6 lg:px-8">
      <div className="sm:mx-auto sm:w-full sm:max-w-lg">
        {/* Header */}
        <div className="text-center mb-4">
          <Link to="/" aria-label="OnlyKickers home" className="inline-block">
            <span className="font-display text-2xl tracking-tight leading-none text-secondary-900">
              ONLY<span className="text-gold-700">KICKERS</span>
            </span>
          </Link>
        </div>

        <div className="bg-white rounded-lg shadow">
          {/* League info header */}
          <div className="px-6 py-6 border-b border-secondary-200">
            <div className="flex flex-wrap items-center gap-2 mb-1">
              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-primary-100 text-primary-700">
                {formatLeagueType(preview.league_type)}
              </span>
              {leagueStarted && (
                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-warning-100 text-warning-700">
                  In Progress
                </span>
              )}
              {leagueFull && !leagueStarted && (
                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-danger-100 text-danger-700">
                  Full
                </span>
              )}
            </div>
            <h1 className="text-2xl font-bold text-secondary-900">{preview.name}</h1>
            <div className="mt-3 flex flex-col gap-1 text-sm text-secondary-600">
              <span>
                <span className="font-medium text-secondary-800">{preview.team_count}</span>
                {' '}of{' '}
                <span className="font-medium text-secondary-800">{preview.max_teams}</span>
                {' '}teams joined
                {preview.spots_available > 0 && (
                  <span className="ml-1 text-primary-600">
                    ({preview.spots_available} spot{preview.spots_available !== 1 ? 's' : ''} left)
                  </span>
                )}
              </span>
              <span>Commissioner: <span className="font-medium text-secondary-800">{preview.commissioner_name}</span></span>
            </div>
          </div>

          {/* Body */}
          <div className="px-6 py-6">
            {/* League has already started */}
            {leagueStarted && (
              <div className="alert-danger" role="alert">
                <p className="font-medium">League has already started</p>
                <p className="mt-1 text-sm">You cannot join a league that is already in progress.</p>
              </div>
            )}

            {/* League is full (but not started) */}
            {!leagueStarted && leagueFull && (
              <div className="rounded-md bg-secondary-50 border border-secondary-200 p-4 text-center">
                <p className="font-medium text-secondary-800">League is Full</p>
                <p className="mt-1 text-sm text-secondary-600">All spots have been taken. Check back if a spot opens up.</p>
              </div>
            )}

            {/* Can join — not authenticated */}
            {canJoin && !user && (
              <div className="text-center space-y-4">
                <div>
                  <h2 className="text-lg font-semibold text-secondary-900">Join this league</h2>
                  <p className="mt-1 text-sm text-secondary-600">Create an account or sign in to claim your spot.</p>
                </div>
                <Link
                  to={`/register?invite=${code}`}
                  className="btn-primary w-full block text-center"
                >
                  Sign up to join
                </Link>
                <p className="text-sm text-secondary-600">
                  Already have an account?{' '}
                  <Link to={`/login?invite=${code}`} className="font-medium text-primary-600 hover:text-primary-500">
                    Sign in
                  </Link>
                </p>
              </div>
            )}

            {/* Can join — authenticated */}
            {canJoin && user && (
              <div className="space-y-4">
                <div>
                  <h2 className="text-lg font-semibold text-secondary-900">You're invited!</h2>
                  <p className="mt-1 text-sm text-secondary-600">Enter a team name to join this league.</p>
                </div>

                {alreadyJoined ? (
                  <div className="rounded-md bg-secondary-50 border border-secondary-200 p-4">
                    <p className="font-medium text-secondary-800">You're already in this league.</p>
                    <Link to="/app/leagues" className="mt-3 inline-block btn-primary text-sm">
                      Go to your leagues
                    </Link>
                  </div>
                ) : (
                  <form onSubmit={handleJoin} className="space-y-4">
                    {joinError && (
                      <div className="alert-danger" role="alert">{joinError}</div>
                    )}
                    <div>
                      <label htmlFor="team-name" className="form-label">
                        Your team name
                      </label>
                      <input
                        id="team-name"
                        type="text"
                        className="form-input"
                        placeholder="Enter team name"
                        value={teamName}
                        onChange={(e) => setTeamName(e.target.value)}
                        required
                        disabled={joining}
                      />
                    </div>
                    <button
                      type="submit"
                      disabled={joining || !teamName.trim()}
                      className="btn-primary w-full"
                    >
                      {joining ? (
                        <span className="flex items-center justify-center gap-2">
                          <span className="loading-spinner w-4 h-4"></span>
                          Joining…
                        </span>
                      ) : (
                        'Join League'
                      )}
                    </button>
                  </form>
                )}
              </div>
            )}
          </div>
        </div>

        <p className="mt-6 text-center text-xs text-secondary-400">
          OnlyKickers — Fantasy football for kickers &amp; punters
        </p>
      </div>
    </div>
  );
}
